import {
  AssessmentStatus,
  UnderstandingGenerationState,
  EngagementStatus,
  DiagramGenerationState,
  ReviewStatus,
  Owner,
  QuestionStatus,
  UserMin,
  ReviewableMin,
} from "platformed-client/types";
import {
  ReviewStatusList,
  QuestionStatusList,
  AssessmentStatusList,
  EngagementStatusList,
} from "platformed-client/lists";

/**
 * Compares two optional values using a provided comparator function.
 *
 * - If both values are null or undefined, they are considered equal.
 * - If one value is null or undefined, it is placed before or after the other value based on the `nullsFirst` parameter (defaults to false).
 * - If both values are non-null, they are compared using the provided comparator function.
 *
 */
export function compareOptional<T>(
  a: T | null | undefined,
  b: T | null | undefined,
  compare: (a: T, b: T) => number,
): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;

  return compare(a, b);
}

export function compareOwnersByName(a: Owner, b: Owner): number {
  return (a.owner_payload.content.name ?? "").localeCompare(b.owner_payload.content.name ?? "");
}

export function compareUsersByName(a: UserMin, b: UserMin): number {
  return (a.name ?? "").localeCompare(b.name ?? "");
}

export function compareReviewables(a: ReviewableMin | undefined, b: ReviewableMin | undefined): number {
  return compareReviewStatus(a?.overall_status ?? "Unrequested", b?.overall_status ?? "Unrequested");
}

const REVIEW_STATUS_ORDER = new Map<ReviewStatus, number>(ReviewStatusList.map((status, index) => [status, index]));

export function compareReviewStatus(a: ReviewStatus, b: ReviewStatus): number {
  return REVIEW_STATUS_ORDER.get(a)! - REVIEW_STATUS_ORDER.get(b)!;
}

const QUESTION_STATUS_ORDER = new Map<QuestionStatus, number>(
  QuestionStatusList.map((status, index) => [status, index]),
);

export function compareQuestionStatuses(a: QuestionStatus, b: QuestionStatus): number {
  return QUESTION_STATUS_ORDER.get(a)! - QUESTION_STATUS_ORDER.get(b)!;
}

const ASSESSMENT_STATUS_ORDER = new Map<AssessmentStatus, number>(
  AssessmentStatusList.map((status, index) => [status, index]),
);

export function compareAssessmentStatuses(a: AssessmentStatus, b: AssessmentStatus): number {
  return ASSESSMENT_STATUS_ORDER.get(a)! - ASSESSMENT_STATUS_ORDER.get(b)!;
}

const DATA_ROOM_STATUS_ORDER = new Map<EngagementStatus, number>(
  EngagementStatusList.map((status, index) => [status, index]),
);

export function compareEngagementStatuses(a: EngagementStatus, b: EngagementStatus): number {
  return DATA_ROOM_STATUS_ORDER.get(a)! - DATA_ROOM_STATUS_ORDER.get(b)!;
}

const DIAGRAM_GENERATION_STATE_ORDER: DiagramGenerationState["type"][] = [
  "Generating",
  "NotStarted",
  "Completed",
  "Error",
];

const DIAGRAM_GENERATION_STATE_ORDER_MAP = new Map<DiagramGenerationState["type"], number>(
  DIAGRAM_GENERATION_STATE_ORDER.map((status, index) => [status, index]),
);

export function compareDiagramGenerationStates(a: DiagramGenerationState, b: DiagramGenerationState): number {
  return DIAGRAM_GENERATION_STATE_ORDER_MAP.get(a.type)! - DIAGRAM_GENERATION_STATE_ORDER_MAP.get(b.type)!;
}

const UNDERSTANDING_GENERATION_STATE_ORDER: UnderstandingGenerationState["type"][] = [
  "Generating",
  "NotStarted",
  "Completed",
  "Error",
];

const UNDERSTANDING_GENERATION_STATE_ORDER_MAP = new Map<UnderstandingGenerationState["type"], number>(
  UNDERSTANDING_GENERATION_STATE_ORDER.map((status, index) => [status, index]),
);

export function compareUnderstandingGenerationStates(
  a: UnderstandingGenerationState,
  b: UnderstandingGenerationState,
): number {
  return UNDERSTANDING_GENERATION_STATE_ORDER_MAP.get(a.type)! - UNDERSTANDING_GENERATION_STATE_ORDER_MAP.get(b.type)!;
}
